import { PortableText } from "@portabletext/react"
import type { PortableTextTextBlock } from "@sanity/types"
import { twMerge } from "tailwind-merge"
import { ContentBox } from "@/components/resources/components/ContentBox"
import { EmbedTweet } from "@/components/resources/components/EmbedTweet"
import { FullWidthLayout, type LayoutAlign } from "@/components/resources/components/layout/Layout"
import { EmbedVideo } from "@/components/resources/components/ui/EmbedVideo"
import type { ContentImage } from "@/components/resources/data/sanity/posts"
import { basicComponents, SanityBasicContentRenderer } from "./SanityContentDefaults"
import { SanityFileLink } from "./SanityFileLink"
import { SanityContentImage, type SanityImageLayoutSizes } from "./SanityImage"
import { SanityTable } from "./SanityTable"

interface Props {
  content: null | PortableTextTextBlock[]
  layoutSizes?: SanityImageLayoutSizes
  align?: LayoutAlign
}

export type { Props as SanityContentRendererProps }

export const SanityContentRenderer = ({ content, layoutSizes, align }: Props) => {
  if (!content) {
    return null
  }
  return (
    <PortableText
      value={content}
      components={{
        ...basicComponents,
        types: {
          poster: ({ value }: { value: ContentImage }) => {
            // TODO: maybe setup image.caption for Figure. For now it's considered a possible alt text too (as a fallback)
            if (!value?.asset) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Content contains an empty image")
              }
              return null
            }

            return (
              <div className="my-4 md:my-6">
                <SanityContentImage
                  key={value._key ?? value.asset._ref}
                  image={value}
                  className="rounded-lg"
                  layoutSizes={layoutSizes}
                  layoutAlign={align}
                />
              </div>
            )
          },
          upload: ({ value }) => {
            if (!value.label || !value.asset) {
              return null
            }

            return <SanityFileLink asset={value.asset}>{value.label}</SanityFileLink>
          },
          youtube: ({ value }) => {
            if (!value?.url) {
              return null
            }

            return <EmbedVideo url={value.url} className="mb-4 md:mb-6 pt-xs md:pt-s" />
          },
          embedTweet: ({ value }) => {
            if (!value?.tweetHtml) {
              return null
            }
            return <EmbedTweet html={value.tweetHtml} className="mb-4 md:mb-6" />
          },
          table: ({ value }) => {
            if (!value?.rows?.length) {
              return null
            }

            return (
              <FullWidthLayout size="wide-content" align={align}>
                <SanityTable rows={value.rows} className="mb-4 md:mb-6 w-full" />
              </FullWidthLayout>
            )
          },
          contentBox: ({ value }) => {
            if (!value?.content) {
              return null
            }

            return (
              // No padding bottom because the content flows naturally
              <>
                {value.title && (
                  <p className={twMerge("leading-none font-medium mb-2 md:mb-4 text-fg-secondary")}>
                    {value.title}
                  </p>
                )}
                <ContentBox className="mb-4">
                  <SanityBasicContentRenderer content={value.content} />
                </ContentBox>
              </>
            )
          },
          contentBreak: ({ value }) => {
            if (!value?.style) {
              return null
            }
            switch (value.style) {
              case "hr":
                return <hr className="my-4 md:my-6 border-none h-px bg-border-default" />
              default:
                return null
            }
          },
        },
      }}
    />
  )
}
